import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, XCircle, Clock, Package } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";

const EST = {
  activo: "bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae]",
  cumplido: "bg-blue-100 text-blue-700",
  cancelado: "bg-gray-200 text-gray-500",
  pendiente: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]",
  procesada: "bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae]",
  rechazada: "bg-[#f5d6cf] text-[#7a2514] border-[#d99584]",
};

export default function Deliveries() {
  return (
    <div className="space-y-6" data-testid="deliveries-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Entregas y devoluciones</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Compromisos de entrega a lo largo del tiempo, entregas parciales programadas y devoluciones de clientes con ajuste automático de comisión.</p>
      </div>
      <Tabs defaultValue="comp">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="comp" data-testid="tab-compromisos"><Clock size={14} className="mr-2" />Compromisos de entrega</TabsTrigger>
          <TabsTrigger value="dev" data-testid="tab-devoluciones"><XCircle size={14} className="mr-2" />Devoluciones de cliente</TabsTrigger>
        </TabsList>
        <TabsContent value="comp"><Compromisos /></TabsContent>
        <TabsContent value="dev"><Devoluciones /></TabsContent>
      </Tabs>
    </div>
  );
}

function Compromisos() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prods, setProds] = useState([]);
  const [open, setOpen] = useState(false);
  const [entregaOpen, setEntregaOpen] = useState(null); // {comp, detalle}
  const [form, setForm] = useState(empty());
  const [ent, setEnt] = useState({ cantidad: 0, fecha: new Date().toISOString().slice(0, 10), observacion: "" });
  function empty() {
    return { cliente_id: "", fecha_inicio: new Date().toISOString().slice(0, 10), fecha_compromiso: "", observacion: "", detalles: [] };
  }
  const load = async () => {
    const [c, cl, pr] = await Promise.all([
      api.get("/deliveries/compromisos"),
      api.get("/masters/clientes"),
      api.get("/masters/productos-comerciales"),
    ]);
    setItems(c.data); setClientes(cl.data.filter(x => x.estado === "aprobado")); setProds(pr.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);
  const addDet = () => setForm(f => ({ ...f, detalles: [...f.detalles, { producto_comercial_id: "", producto_nombre: "", cantidad_comprometida: 0, unidad_venta: "unidad" }] }));
  const updDet = (i, field, val) => setForm(f => {
    const n = [...f.detalles];
    n[i] = { ...n[i], [field]: val };
    if (field === "producto_comercial_id") {
      const p = prods.find(x => x.id === val);
      if (p) n[i].producto_nombre = p.nombre_comercial;
    }
    return { ...f, detalles: n };
  });
  const save = async () => {
    if (!form.cliente_id || !form.fecha_compromiso || form.detalles.length === 0) return toast.error("Complete cliente, fecha y al menos un producto");
    try { await api.post("/deliveries/compromisos", form); toast.success("Compromiso creado"); setOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const registrar = async () => {
    try {
      await api.post(`/deliveries/compromisos/${entregaOpen.comp.id}/registrar-entrega`, {
        detalle_id: entregaOpen.detalle.id, cantidad: ent.cantidad, fecha: ent.fecha, observacion: ent.observacion,
      });
      toast.success("Entrega registrada"); setEntregaOpen(null); setEnt({ cantidad: 0, fecha: new Date().toISOString().slice(0, 10), observacion: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} className="bg-[#205c45]" data-testid="new-compromiso-btn"><Plus size={16} className="mr-2" />Nuevo compromiso</Button></div>
      <div className="space-y-3">
        {items.map(c => (
          <div key={c.id} className="rounded-md border border-[#d1dbd6] bg-white p-4" data-testid={`comp-${c.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-semibold">{c.numero}</span>
                  <Badge className={`${EST[c.estado] || ""} capitalize`}>{c.estado}</Badge>
                </div>
                <div className="font-semibold text-[#15241d] mt-1">{c.cliente_nombre}</div>
                <div className="text-xs text-[#5c7368]">Inicio: {c.fecha_inicio} · Compromiso: <strong>{c.fecha_compromiso}</strong></div>
              </div>
              {c.estado === "activo" && (
                <Button size="sm" variant="outline" onClick={() => api.post(`/deliveries/compromisos/${c.id}/cancelar`).then(() => { toast.success("Cancelado"); load(); }).catch(e => toast.error(formatApiError(e)))}>Cancelar</Button>
              )}
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="text-right w-28">Comprometido</th><th className="text-right w-28">Entregado</th><th className="text-right w-28">Pendiente</th><th className="w-32"></th></tr></thead>
              <tbody>
                {c.detalles.map(d => (
                  <tr key={d.id}>
                    <td>{d.producto_nombre}</td>
                    <td className="text-right tabular-nums">{d.cantidad_comprometida}</td>
                    <td className="text-right tabular-nums text-[#1f6b42]">{d.cantidad_entregada}</td>
                    <td className="text-right tabular-nums font-semibold">{d.cantidad_pendiente}</td>
                    <td className="text-right">
                      {d.cantidad_pendiente > 0 && c.estado === "activo" && (
                        <Button size="sm" variant="outline" onClick={() => { setEntregaOpen({ comp: c, detalle: d }); setEnt({ cantidad: d.cantidad_pendiente, fecha: new Date().toISOString().slice(0,10), observacion: "" }); }}><CheckCircle2 size={12} className="mr-1" />Registrar</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {c.detalles.some(d => d.entregas?.length > 0) && (
              <div className="mt-3 pt-3 border-t text-xs text-[#5c7368]">
                <strong>Entregas parciales realizadas:</strong>
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                  {c.detalles.flatMap(d => (d.entregas || []).map((e, i) => <li key={d.id + i}>#{e.numero_entrega} · {d.producto_nombre} · {e.cantidad_entregada} uds el {e.fecha_entregada}</li>))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-10 text-[#8ba397] bg-white rounded-md border border-[#d1dbd6]">Sin compromisos registrados</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo compromiso de entrega</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Cliente</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(v) => setForm({ ...form, cliente_id: v })}
                clientes={clientes}
                placeholder="Buscar cliente…"
                testId="delivery-client-1"
              />
            </div>
            <div><Label>Fecha inicio</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
            <div><Label>Fecha compromiso</Label><Input type="date" value={form.fecha_compromiso} onChange={(e) => setForm({ ...form, fecha_compromiso: e.target.value })} /></div>
            <div className="col-span-2"><Label>Observación</Label><Input value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos comprometidos</div>
              <Button size="sm" variant="outline" onClick={addDet}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="w-28">Unidad</th><th className="w-28 text-right">Cantidad</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.detalles.map((d, i) => (
                  <tr key={i}>
                    <td><Select value={d.producto_comercial_id} onValueChange={(v) => updDet(i, "producto_comercial_id", v)}><SelectTrigger className="h-9"><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>)}</SelectContent></Select></td>
                    <td><Select value={d.unidad_venta} onValueChange={(v) => updDet(i, "unidad_venta", v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unidad">Unidad</SelectItem><SelectItem value="presentacion">Presentación</SelectItem></SelectContent></Select></td>
                    <td><Input className="h-9 text-right" type="number" value={d.cantidad_comprometida} onChange={(e) => updDet(i, "cantidad_comprometida", parseFloat(e.target.value) || 0)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({ ...f, detalles: f.detalles.filter((_, x) => x !== i) }))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-compromiso-btn">Guardar compromiso</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!entregaOpen} onOpenChange={(o) => !o && setEntregaOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registrar entrega parcial</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4a5c53] mb-2">{entregaOpen?.detalle?.producto_nombre} · Pendiente: <strong>{entregaOpen?.detalle?.cantidad_pendiente}</strong></p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cantidad</Label><Input type="number" value={ent.cantidad} onChange={(e) => setEnt({ ...ent, cantidad: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Fecha</Label><Input type="date" value={ent.fecha} onChange={(e) => setEnt({ ...ent, fecha: e.target.value })} /></div>
            <div className="col-span-2"><Label>Observación</Label><Input value={ent.observacion} onChange={(e) => setEnt({ ...ent, observacion: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEntregaOpen(null)}>Cancelar</Button><Button onClick={registrar} className="bg-[#205c45]" data-testid="save-entrega-btn">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Devoluciones() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prods, setProds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() {
    return { cliente_id: "", factura_id: "", vendedor_id: "", fecha: new Date().toISOString().slice(0, 10), motivo: "", detalles: [] };
  }
  const load = async () => {
    const [d, cl, pr] = await Promise.all([
      api.get("/deliveries/devoluciones"), api.get("/masters/clientes"), api.get("/masters/productos-comerciales"),
    ]);
    setItems(d.data); setClientes(cl.data); setProds(pr.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);
  const addDet = () => setForm(f => ({ ...f, detalles: [...f.detalles, { producto_comercial_id: "", producto_nombre: "", numero_lote: "", cantidad: 0, precio_unitario: 0, motivo_detalle: "" }] }));
  const updDet = (i, field, val) => setForm(f => {
    const n = [...f.detalles]; n[i] = { ...n[i], [field]: val };
    if (field === "producto_comercial_id") {
      const p = prods.find(x => x.id === val);
      if (p) { n[i].producto_nombre = p.nombre_comercial; n[i].precio_unitario = p.precio_venta || 0; }
    }
    return { ...f, detalles: n };
  });
  const save = async () => {
    if (!form.cliente_id || !form.motivo || form.detalles.length === 0) return toast.error("Complete cliente, motivo y productos");
    try { await api.post("/deliveries/devoluciones", form); toast.success("Devolución registrada"); setOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const aprobar = (it) => api.post(`/deliveries/devoluciones/${it.id}/aprobar`).then(() => { toast.success("Devolución procesada"); load(); }).catch(e => toast.error(formatApiError(e)));
  const rechazar = (it) => api.post(`/deliveries/devoluciones/${it.id}/rechazar`).then(() => { toast.success("Rechazada"); load(); }).catch(e => toast.error(formatApiError(e)));
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} className="bg-[#205c45]" data-testid="new-devolucion-btn"><Plus size={16} className="mr-2" />Nueva devolución</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Motivo</th><th className="text-right">Total</th><th>Estado</th><th className="w-36"></th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td>{it.cliente_nombre}</td>
                <td className="max-w-[240px] truncate" title={it.motivo}>{it.motivo}</td>
                <td className="text-right tabular-nums">${it.total_valor?.toFixed(2) || 0}</td>
                <td><Badge className={`${EST[it.estado] || ""} capitalize`}>{it.estado}</Badge></td>
                <td>
                  {it.estado === "pendiente" && (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" onClick={() => aprobar(it)} className="bg-[#205c45]" data-testid={`approve-dev-${it.id}`}>Aprobar</Button>
                      <Button size="sm" variant="outline" onClick={() => rechazar(it)}>Rechazar</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin devoluciones</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva devolución de cliente</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Cliente</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(v) => setForm({ ...form, cliente_id: v })}
                clientes={clientes}
                placeholder="Buscar cliente…"
                testId="delivery-client-2"
              />
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>Factura origen (opcional)</Label><Input value={form.factura_id} onChange={(e) => setForm({ ...form, factura_id: e.target.value })} placeholder="ID factura" /></div>
            <div><Label>Vendedor (para ajuste comisión)</Label><Input value={form.vendedor_id} onChange={(e) => setForm({ ...form, vendedor_id: e.target.value })} placeholder="ID vendedor" /></div>
            <div className="col-span-3"><Label>Motivo</Label><Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Caducidad, defecto, devolución comercial..." /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos a devolver</div>
              <Button size="sm" variant="outline" onClick={addDet}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="w-24">Lote</th><th className="w-20 text-right">Cant.</th><th className="w-24 text-right">Precio U.</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.detalles.map((d, i) => (
                  <tr key={i}>
                    <td><Select value={d.producto_comercial_id} onValueChange={(v) => updDet(i, "producto_comercial_id", v)}><SelectTrigger className="h-9"><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>)}</SelectContent></Select></td>
                    <td><Input className="h-9" value={d.numero_lote} onChange={(e) => updDet(i, "numero_lote", e.target.value)} /></td>
                    <td><Input className="h-9 text-right" type="number" value={d.cantidad} onChange={(e) => updDet(i, "cantidad", parseFloat(e.target.value) || 0)} /></td>
                    <td><Input className="h-9 text-right" type="number" step="0.01" value={d.precio_unitario} onChange={(e) => updDet(i, "precio_unitario", parseFloat(e.target.value) || 0)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({ ...f, detalles: f.detalles.filter((_, x) => x !== i) }))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-dev-btn">Guardar devolución</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
